// Customer Group create/edit dialog body.
//
// Rendered as the `content` of the shared ActionDialog (see
// `src/components/action-dialog.tsx`) on customer-groups-page.tsx.
// The dialog owns its own form state — the page just toggles
// open/closed.

import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material'
import { Button, Stack } from 'components'
import { useCustomerTypeahead } from 'services/queries/use-customer-typeahead'
import { useCreateCustomerGroup } from 'services/queries/use-create-customer-group'
import { useUpdateCustomerGroup } from 'services/queries/use-update-customer-group'
import { CustomerGroupT, CustomerTypeaheadResultT } from 'types/global'
import {
  formatCustomerOptionLabel,
  formatMemberCountLabel,
  sameCustomerIdSet,
} from './helpers'

// Field caps mirror the CMAPI validator (createCustomerGroupSchema).
const MAX_NAME_LENGTH = 200
const MAX_DESCRIPTION_LENGTH = 1000
const TYPEAHEAD_DEBOUNCE_MS = 250

interface CustomerGroupDialogProps {
  closeFn: () => void
  // Editing an existing group? Pass it in — the form pre-fills and
  // the submit action becomes a PUT. Undefined means create.
  group?: CustomerGroupT
}

/**
 * Picker option as it appears in the Autocomplete dropdown. We
 * deliberately store the full row (id + label fields) so chips can
 * render the operator-friendly label without a second fetch.
 */
type MemberOption = CustomerTypeaheadResultT

const CustomerGroupDialog: React.FC<CustomerGroupDialogProps> = ({
  closeFn,
  group,
}) => {
  const isEdit = Boolean(group)

  const [name, setName] = useState(group?.name ?? '')
  const [description, setDescription] = useState(group?.description ?? '')

  // Selected chips. Seeded from the row on edit; the typeahead can't
  // re-derive the labels from `customer_ids` alone, so the seed uses
  // a placeholder label that gets replaced as soon as the operator
  // searches and re-picks anyone they want to keep.
  const [members, setMembers] = useState<MemberOption[]>(() => {
    if (!group) return []
    return group.customer_ids.map((id) => ({
      id,
      local_id: String(id),
      name: '(existing member)',
      country: '',
    }))
  })

  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedSearch(searchInput),
      TYPEAHEAD_DEBOUNCE_MS,
    )
    return () => clearTimeout(handle)
  }, [searchInput])

  const { data: typeaheadResponse, isFetching: isTypeaheadLoading } =
    useCustomerTypeahead(debouncedSearch)

  const typeaheadOptions = useMemo<MemberOption[]>(() => {
    if (!typeaheadResponse || !typeaheadResponse.success) return []
    // Filter out anyone the operator has already picked so the
    // dropdown doesn't show duplicates.
    const selected = new Set(members.map((m) => m.id))
    return typeaheadResponse.data.filter((row) => !selected.has(row.id))
  }, [typeaheadResponse, members])

  const createMutation = useCreateCustomerGroup()
  const updateMutation = useUpdateCustomerGroup()
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading

  const trimmedName = name.trim()
  const trimmedDescription = description.trim()

  // The CMAPI enforces this too — the disable is just nicer UX
  // than letting the operator click Save and watch a snackbar fire.
  const hasMembers = members.length > 0
  const nameTooLong = name.length > MAX_NAME_LENGTH
  const descriptionTooLong = description.length > MAX_DESCRIPTION_LENGTH
  const canSubmit =
    trimmedName.length > 0 &&
    hasMembers &&
    !nameTooLong &&
    !descriptionTooLong &&
    !isSubmitting

  const hasChangesFromOriginal = useMemo(() => {
    if (!group) return true
    const currentIds = members.map((m) => m.id)
    const sameName = trimmedName === group.name
    const sameDescription =
      trimmedDescription === (group.description?.trim() ?? '')
    const sameMembers = sameCustomerIdSet(currentIds, group.customer_ids)
    return !(sameName && sameDescription && sameMembers)
  }, [group, trimmedName, trimmedDescription, members])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return

    const customerIds = members.map((m) => m.id)
    const payload = {
      name: trimmedName,
      description: trimmedDescription === '' ? null : trimmedDescription,
      customer_ids: customerIds,
    }

    if (isEdit && group) {
      updateMutation.mutate(
        { id: group.id, body: payload },
        {
          onSuccess: (response) => {
            if (response.success) closeFn()
          },
        },
      )
    } else {
      createMutation.mutate(payload, {
        onSuccess: (response) => {
          if (response.success) closeFn()
        },
      })
    }
  }

  return (
    <form onSubmit={handleSubmit} data-testid="customer-group-form">
      <Box
        sx={{
          px: 3,
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        <Box>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Q2 trial customers"
            required
            fullWidth
            size="small"
            inputProps={{ maxLength: MAX_NAME_LENGTH + 1 }}
            error={nameTooLong}
            helperText={
              nameTooLong
                ? `Name must be ${MAX_NAME_LENGTH} characters or fewer`
                : 'Required. Names are unique across the platform (case-insensitive).'
            }
          />
        </Box>

        <Box>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this group used for?"
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            size="small"
            inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH + 1 }}
            error={descriptionTooLong}
            helperText={
              descriptionTooLong
                ? `Description must be ${MAX_DESCRIPTION_LENGTH} characters or fewer`
                : 'Optional.'
            }
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            Members
          </Typography>
          <Autocomplete<MemberOption, true, false, false>
            multiple
            options={typeaheadOptions}
            value={members}
            onChange={(_e, newValue) => setMembers(newValue)}
            inputValue={searchInput}
            onInputChange={(_e, newValue) => setSearchInput(newValue)}
            getOptionLabel={formatCustomerOptionLabel}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            filterOptions={(opts) => opts}
            loading={isTypeaheadLoading}
            noOptionsText={
              debouncedSearch.trim().length === 0
                ? 'Start typing to search customers'
                : 'No matching customers'
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={formatCustomerOptionLabel(option)}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by company name or local ID..."
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isTypeaheadLoading ? (
                        <CircularProgress size={16} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.75 }}>
            At least one customer is required.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Typography
          sx={{ fontSize: 13, color: 'text.secondary' }}
          data-testid="customer-group-member-count"
        >
          {formatMemberCountLabel(members.length)}
        </Typography>
        <Stack spacing={1}>
          <Button
            variant="text"
            onClick={closeFn}
            data-cy="customer-group-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!canSubmit || (isEdit && !hasChangesFromOriginal)}
            loading={isSubmitting}
            data-cy="customer-group-save"
          >
            {isEdit ? 'Save changes' : 'Create group'}
          </Button>
        </Stack>
      </Box>
    </form>
  )
}

export default CustomerGroupDialog
